import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import configureStore from 'nallian-shared-ui/lib/store/configureStore';
import { basename } from 'nallian-shared-ui/lib/lib/path';
import { getApplicationInsights } from 'nallian-shared-ui/lib/config/applicationInsights';
import Layout from 'nallian-shared-ui/lib/layout';
import 'nallian-shared-ui/lib/config/i18n';
import { createMsalInstance } from 'nallian-shared-ui/lib/authentication/helpers/createMsalInstance';
import { msalConfig } from 'nallian-shared-ui/lib/authentication/constants/config';
import { MsalProvider } from '@azure/msal-react';
import MsalToken from 'nallian-shared-ui/lib/authentication/MsalToken';
import Logout from 'nallian-shared-ui/lib/authentication/Logout';
import routes from './app/config/routes';
import translations from './app/config/translations';
import rootReducer from './rootReducer';
import rootSaga from './rootSaga';
import RequestToJoin from './portal/joinRequest/components/RequestToJoin';
import LandingPage from './portal/landing/components/LandingPage';
import WithTranslations from './portal/withTranslations';
import Invitation from './portal/onboarding/components/Invitation';
import { GlobalStyle } from './mgmt/app/styles'; // For IE 11 support
import { ImpersonationBanner } from 'nallian-shared-ui/lib/impersonation';
import apiRoutes from './app/api/routes';
import NavigationSetup from 'nallian-shared-ui/lib/components/NavigationSetup';

const VERSION_CONFIG = { url: apiRoutes.version(), showInPopup: true };

// Function to load the dynamic script
const loadDynamicScript = () => {
  return new Promise((resolve, reject) => {
    const url = window.location.hostname;
    const formattedUrl = url.replace(/\./g, '_');
    const srcUrl = `${window.location.origin}${basename}/_configs/${formattedUrl}.js`;
    const script = document.createElement('script');
    script.src = srcUrl;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const init = async () => {
  const insights = getApplicationInsights();

  if (insights)
    insights.trackTrace({
      message: `client-id before loading dynamic script: ${window.appSettings.clientid}`,
      severityLevel: SeverityLevel.Information,
    });

  await loadDynamicScript();

  if (insights)
    insights.trackTrace({
      message: `client-id after loading dynamic script: ${window.appSettings.clientid}`,
      severityLevel: SeverityLevel.Information,
    });

  const store = configureStore(rootReducer, rootSaga);

  const customConfig = {
    ...msalConfig,
    auth: {
      ...msalConfig.auth,
      clientId: window.appSettings.clientid,
      redirectUri: `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }${basename}/signIncallback`,
    },
  };

  const msalInstance = createMsalInstance(customConfig);

  const DebugRouter = ({ children }) => {
    const location = useLocation();
    if (process.env.NODE_ENV === 'development') {
      console.log(
        `Route: ${location.pathname}${location.search}, State: ${JSON.stringify(location.state)}`
      );
    }

    return children;
  };

  function ProtectedLayout() {
    return (
      <MsalToken>
        <DebugRouter>
          <>
            <GlobalStyle />
            <ImpersonationBanner url={apiRoutes.impersonationSession()} />
            <Layout
              routes={routes}
              applicationName="Nallian Portal"
              translations={translations}
              version={VERSION_CONFIG}
              showUserMenuApps
            />
          </>
        </DebugRouter>
      </MsalToken>
    );
  }

  const container = document.getElementById('root');
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <Router basename={basename}>
        <WithTranslations translations={translations}>
          <MsalProvider instance={msalInstance}>
            <NavigationSetup />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/request-to-join" element={<RequestToJoin />} />
              <Route path="/invite/:token" element={<Invitation />} />
              <Route path="/sign-out" element={<Logout />} />
              <Route path="/*" element={<ProtectedLayout />} />
            </Routes>
          </MsalProvider>
        </WithTranslations>
      </Router>
    </Provider>
  );
};

init();
